/**
 * AADHYAA — Cookie consent banner
 *
 * Lightweight (no external dependency) GDPR-aligned consent banner.
 * Shows once until accepted/dismissed; choice is stored in localStorage.
 *
 * What we track at "Accept all": session JWT cookie + analytics page-views.
 * What we DON'T track without consent: anything beyond first-party session.
 *
 * The footer link "Manage cookies" re-opens this banner at any time.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "@phosphor-icons/react";

const STORAGE_KEY = "aadhyaa_cookie_consent_v1";

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        // Defer 800ms so it doesn't fight the hero animation
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch {
      // localStorage may be disabled in private mode — fall back to show
      setVisible(true);
    }
    // Allow re-opening via window event
    const handler = () => setVisible(true);
    window.addEventListener("aadhyaa:reopen-cookie-consent", handler);
    return () => window.removeEventListener("aadhyaa:reopen-cookie-consent", handler);
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, ts: new Date().toISOString() })
      );
    } catch {
      /* swallow */
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.35 }}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[200] w-[calc(100vw-2rem)] max-w-2xl"
          data-testid="cookie-consent-banner"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
        >
          <div className="bg-white rounded-2xl border border-[var(--palladian)]/40 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.18)] p-4 sm:p-5">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[var(--burning-flame)]/10 flex items-center justify-center">
                <Cookie size={20} className="text-[var(--burning-flame)]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-cinzel text-sm sm:text-base font-semibold text-[var(--text-primary)] mb-1">
                  We respect your privacy
                </h3>
                <p className="text-xs sm:text-sm text-[var(--text-secondary)] leading-relaxed mb-3">
                  We use a small session cookie to keep you signed in and (optionally)
                  cookies to understand which features help users most. Astrological
                  charts and family data stay on our servers — never sold, never shared.{" "}
                  <a
                    href="/privacy"
                    className="underline decoration-dotted underline-offset-2 hover:text-[var(--truffle)]"
                    data-testid="cookie-consent-privacy-link"
                  >
                    Privacy policy
                  </a>
                  .
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => persist("essential")}
                    className="px-3 py-1.5 text-xs font-medium rounded-full border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)] transition"
                    data-testid="cookie-consent-essential"
                  >
                    Essential only
                  </button>
                  <button
                    onClick={() => persist("all")}
                    className="px-3 py-1.5 text-xs font-semibold rounded-full bg-[var(--burning-flame)] text-white hover:bg-[var(--burning-flame)]/90 transition"
                    data-testid="cookie-consent-accept-all"
                  >
                    Accept all
                  </button>
                </div>
              </div>
              <button
                onClick={() => persist("dismissed")}
                className="flex-shrink-0 w-7 h-7 rounded-full hover:bg-[var(--bg-surface)] flex items-center justify-center text-[var(--text-muted)]"
                aria-label="Close"
                data-testid="cookie-consent-close"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
